import React, { useEffect, useState } from "react";
import { CreateCommanWay, GetCommanWay, ListCommanWay, UpdateCommanWay } from "API/commonApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment";
import UseToast from "utils/AfToast";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import Spinner from "utils/Spinner";
import { Button } from "@heroui/react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, PlusIcon, TrashIcon, DocumentArrowDownIcon, PencilIcon } from "@heroicons/react/24/outline";
import handleWheel from "utils/handleOnWheel";
import { STOCK_ITEM_OPTIONS } from "utils/constant";

// ── Compact select styles (matching AddBooking/Pickup design) ──
const poSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    borderRadius: "10px",
    borderColor: state.isFocused ? "#6366f1" : "#e2e8f0",
    minHeight: "30px",
    fontSize: "12px",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(99,102,241,0.15)" : "none",
    "&:hover": { borderColor: "#cbd5e1" },
    backgroundColor: "white",
    transition: "all 0.15s ease",
  }),
  valueContainer: (base: any) => ({ ...base, padding: "0 6px" }),
  input: (base: any) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base: any) => ({ ...base, height: "30px" }),
  dropdownIndicator: (base: any) => ({ ...base, padding: "4px" }),
  clearIndicator: (base: any) => ({ ...base, padding: "4px" }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: "12px",
    padding: "6px 10px",
    backgroundColor: state.isSelected ? "#6366f1" : state.isFocused ? "#f1f5f9" : "white",
    color: state.isSelected ? "white" : "#334155",
    "&:active": { backgroundColor: "#e0e7ff" },
  }),
  menu: (base: any) => ({ ...base, borderRadius: "12px", overflow: "hidden", boxShadow: "0 10px 40px rgba(0,0,0,0.1)", zIndex: 9999 }),
  menuList: (base: any) => ({ ...base, padding: "4px" }),
  singleValue: (base: any) => ({ ...base, color: "#334155" }),
  placeholder: (base: any) => ({ ...base, color: "#94a3b8", fontSize: "12px" }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
};

// ── Compact form components ──
const MInput = ({ label, name, value, onChange, type = "text", disabled = false, className = "", placeholder = "", min, max }: any) => (
  <div className={`flex items-center gap-1.5 ${className}`}>
    <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap min-w-fit">{label}</label>
    <input
      type={type}
      className={`flex-1 min-w-0 px-2 py-[4px] rounded-lg border text-[13px] outline-none transition-all ${
        disabled
          ? "border-slate-200 bg-slate-100 text-slate-500"
          : "border-slate-200 bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 text-slate-700"
      }`}
      name={name}
      value={value || ""}
      onChange={onChange}
      onWheel={handleWheel}
      placeholder={placeholder || label}
      disabled={disabled}
      min={min}
      max={max}
    />
  </div>
);

const MSelect = ({ label, name, options, value, onChange, disabled = false, className = "" }: any) => (
  <div className={`flex items-center gap-1.5 ${className}`}>
    <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap min-w-fit">{label}</label>
    <Select
      className="flex-1 min-w-0"
      options={options}
      value={value}
      onChange={onChange}
      isDisabled={disabled}
      styles={poSelectStyles}
      menuPortalTarget={document.body}
      menuPosition="fixed"
    />
  </div>
);

// Stock item options + "type manually" variant for CreatableSelect
const STOCK_ITEM_OPTIONS_PO = STOCK_ITEM_OPTIONS.map((o) =>
  o.value === "OTHER" ? { ...o, label: "OTHER (Type manually)" } : o
);

const unitOptions = [
  { value: "Pcs", label: "Pcs" },
  { value: "Kg", label: "Kg" },
  { value: "Ltr", label: "Ltr" },
  { value: "Mtr", label: "Mtr" },
  { value: "Box", label: "Box" },
  { value: "Nos", label: "Nos" },
  { value: "Set", label: "Set" },
  { value: "Roll", label: "Roll" },
];

const paymentTermOptions = [
  { value: "30 Days", label: "30 Days" },
  { value: "15 Days", label: "15 Days" },
  { value: "7 Days", label: "7 Days" },
  { value: "60 Days", label: "60 Days" },
  { value: "90 Days", label: "90 Days" },
  { value: "Full Advance", label: "Full Advance" },
  { value: "Half Advance", label: "Half Advance" },
  { value: "Against Delivery", label: "Against Delivery" },
  { value: "-", label: "-" },
];

interface LineItem {
  description: string;
  hsn: string;
  qty: number;
  unit: string;
  rate: number;
  gstPercent: number;
  amount: number;
}

const emptyItem: LineItem = { description: "", hsn: "", qty: 1, unit: "Pcs", rate: 0, gstPercent: 18, amount: 0 };

const PurchaseOrderCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = !!id && location.pathname.includes("/edit/");
  const isView = !!id && !location.pathname.includes("/edit/");
  const BASE_URL = process.env.REACT_APP_BACKEND_URL;

  // Form state
  const [vendorId, setVendorId] = useState<any>(null);
  const [poDate, setPoDate] = useState(moment().format("YYYY-MM-DD"));
  const [billToSubbranch, setBillToSubbranch] = useState<any>(null);
  const [shipToBranch, setShipToBranch] = useState<any>(null);
  const [shipToManual, setShipToManual] = useState({ name: "", address: "", city: "", state: "", pincode: "", phone: "" });
  const [useManualShip, setUseManualShip] = useState(false);
  const [quotationRef, setQuotationRef] = useState("");
  const [quotationDate, setQuotationDate] = useState("");
  const [paymentTerms, setPaymentTerms] = useState<any>(paymentTermOptions[0]);
  const [notes, setNotes] = useState("");
  const [shippingCharges, setShippingCharges] = useState(0);
  const [items, setItems] = useState<LineItem[]>([{ ...emptyItem }]);

  // Options
  const [vendorOptions, setVendorOptions] = useState<any[]>([]);
  const [subbranchOptions, setSubbranchOptions] = useState<any[]>([]);
  const [branchOptions, setBranchOptions] = useState<any[]>([]);

  // Fetch vendors
  useQuery(
    ["vendorListForPO"],
    () => ListCommanWay("/admin/vendor/list", {}, { page: 1, paginate: 1000 }),
    {
      onSuccess: (res: any) => {
        if (res?.status === "SUCCESS") {
          const opts = (res?.data?.data || []).map((v: any) => ({
            value: v.id,
            label: v.vendor_name || v.companyName || `Vendor #${v.id}`,
          }));
          setVendorOptions(opts);
        }
      },
    },
  );

  // Fetch subbranches for bill-to
  useQuery(
    ["subbranchListForPO"],
    () => ListCommanWay("/admin/subbranch/list", {}, { paginate: 1000 }),
    {
      onSuccess: (res: any) => {
        if (res?.status === "SUCCESS") {
          const opts = (res?.data?.data || []).map((b: any) => ({
            value: b.id,
            label: b.branch_name || `Branch #${b.id}`,
          }));
          setSubbranchOptions(opts);
          setBranchOptions(opts);
        }
      },
    },
  );

  // Fetch existing PO for edit/view
  const { data: poResp, isLoading: loadingPO } = useQuery(
    ["purchaseOrderDetail", id],
    () => GetCommanWay(`/admin/purchase-order/${id}`, {}, {}),
    {
      enabled: isEdit || isView,
      onSuccess: (res: any) => {
        if (res?.status === "SUCCESS" && res?.data) {
          const d = res.data;
          const parsedShipToManual = typeof d.shipToManual === "string" ? JSON.parse(d.shipToManual) : d.shipToManual;
          const parsedItems = typeof d.items === "string" ? JSON.parse(d.items) : d.items;
          setPoDate(d.poDate ? moment(d.poDate).format("YYYY-MM-DD") : "");
          if (parsedShipToManual && Object.keys(parsedShipToManual).length) {
            setUseManualShip(true);
            setShipToManual(parsedShipToManual);
          }
          setQuotationRef(d.quotationRef || "");
          setQuotationDate(d.quotationDate ? moment(d.quotationDate).format("YYYY-MM-DD") : "");
          setPaymentTerms(d.paymentTerms ? { value: d.paymentTerms, label: d.paymentTerms } : paymentTermOptions[0]);
          setNotes(d.notes || "");
          setShippingCharges(d.shippingCharges || 0);
          if (Array.isArray(parsedItems) && parsedItems.length) setItems(parsedItems);
        }
      },
    },
  );

  // Resolve dropdown labels from pre-fetched options once both PO data & options are loaded
  useEffect(() => {
    const d = poResp?.status === "SUCCESS" ? poResp?.data : null;
    if (!d) return;
    if (vendorOptions.length && d.vendorId) {
      const found = vendorOptions.find((o: any) => o.value === d.vendorId);
      setVendorId(found || { value: d.vendorId, label: `Vendor #${d.vendorId}` });
    }
    if (subbranchOptions.length && d.billToSubbranch) {
      const found = subbranchOptions.find((o: any) => o.value === d.billToSubbranch);
      setBillToSubbranch(found || { value: d.billToSubbranch, label: `Branch #${d.billToSubbranch}` });
    }
    if (branchOptions.length && d.shipToBranch) {
      const found = branchOptions.find((o: any) => o.value === d.shipToBranch);
      setShipToBranch(found || { value: d.shipToBranch, label: `Branch #${d.shipToBranch}` });
    }
  }, [poResp, vendorOptions, subbranchOptions, branchOptions]);

  // Line item handlers
  const addItem = () => setItems([...items, { ...emptyItem }]);

  const removeItem = (idx: number) => {
    if (items.length === 1) return;
    setItems(items.filter((_, i) => i !== idx));
  };

  const updateItem = (idx: number, field: keyof LineItem, value: any) => {
    const updated = [...items];
    (updated[idx] as any)[field] = value;
    if (field === "qty" || field === "rate" || field === "gstPercent") {
      const base = updated[idx].qty * updated[idx].rate;
      updated[idx].amount = base + (base * updated[idx].gstPercent) / 100;
    }
    setItems(updated);
  };

  // Totals
  const subtotal = items.reduce((sum, item) => sum + item.qty * item.rate, 0);
  const totalGst = items.reduce((sum, item) => sum + (item.qty * item.rate * item.gstPercent) / 100, 0);
  const grandTotal = subtotal + totalGst + shippingCharges;

  // Create mutation
  const { mutate: createPO, isLoading: creating } = useMutation(
    (data: any) => CreateCommanWay({ url: "/admin/purchase-order/create", data }),
    {
      onSuccess: (res: any) => {
        if (res?.status === "SUCCESS") {
          UseToast("Purchase Order created successfully", "success");
          navigate("/purchase-order/list");
        } else {
          UseToast(res?.message || "Failed to create PO", "error");
        }
      },
      onError: (err: any) => UseToast(typeof err === "string" ? err : err?.message || "Error creating PO", "error"),
    },
  );

  // Update mutation
  const { mutate: updatePO, isLoading: updating } = useMutation(
    (data: any) => UpdateCommanWay({ url: `/admin/purchase-order/update/${id}`, data }),
    {
      onSuccess: (res: any) => {
        if (res?.status === "SUCCESS") {
          UseToast("Purchase Order updated successfully", "success");
          navigate(`/purchase-order/${id}`);
        } else {
          UseToast(res?.message || "Failed to update PO", "error");
        }
      },
      onError: (err: any) => UseToast(typeof err === "string" ? err : err?.message || "Error updating PO", "error"),
    },
  );

  const handleSubmit = () => {
    if (!vendorId) {
      UseToast("Please select a vendor", "error");
      return;
    }
    if (!poDate) {
      UseToast("Please select PO date", "error");
      return;
    }
    if (items.length === 0 || !items[0].description) {
      UseToast("Please add at least one item", "error");
      return;
    }

    const payload: any = {
      vendorId: vendorId.value,
      poDate,
      billToSubbranch: billToSubbranch?.value || undefined,
      shipToBranch: !useManualShip ? shipToBranch?.value || undefined : undefined,
      shipToManual: useManualShip ? shipToManual : undefined,
      quotationRef: quotationRef || undefined,
      quotationDate: quotationDate || undefined,
      paymentTerms: paymentTerms?.value || "30 Days",
      items,
      subtotal,
      totalGst,
      shippingCharges,
      grandTotal,
      notes: notes || undefined,
    };

    if (isEdit) {
      updatePO(payload);
    } else {
      createPO(payload);
    }
  };

  if ((isEdit || isView) && loadingPO) {
    return (
      <div className="flex justify-center py-20">
        <Spinner />
      </div>
    );
  }

  const handleDownloadPdf = () => {
    const poNo = poResp?.data?.poNo;
    if (!poNo) return UseToast("PO number not available", "error");
    window.open(`${BASE_URL}/purchase_orders/${poNo}.pdf`, "_blank");
  };

  return (
    <div className="px-3 py-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="p-1 rounded-lg hover:bg-slate-100 text-slate-500">
            <ArrowLeftIcon className="w-4 h-4" />
          </button>
          <h1 className="text-[15px] font-bold text-slate-800">{isView ? (poResp?.data?.poNo || "Purchase Order") : isEdit ? "Edit Purchase Order" : "New Purchase Order"}</h1>
        </div>
        <div className="flex gap-2">
          {isView ? (
            <>
              <Button size="sm" variant="bordered" onPress={handleDownloadPdf} startContent={<DocumentArrowDownIcon className="w-4 h-4" />} className="text-xs">
                PDF
              </Button>
              <Button size="sm" variant="bordered" onPress={() => navigate(`/purchase-order/edit/${id}`)} startContent={<PencilIcon className="w-4 h-4" />} className="text-xs">
                Edit
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="bordered" onPress={() => navigate(-1)} className="text-xs">
                Cancel
              </Button>
              <Button size="sm" color="primary" onPress={handleSubmit} isLoading={creating || updating} className="text-xs">
                {isEdit ? "Update PO" : "Create PO"}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* ── Main Form ── */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4">

        {/* ─── Row 1: Vendor & Dates ─── */}
        <div className="grid grid-cols-6 md:grid-cols-2 gap-x-3 gap-y-1">
          <MSelect
            label="Vendor"
            name="vendorId"
            options={vendorOptions}
            value={vendorId}
            onChange={(val: any) => setVendorId(val)}
            disabled={isView}
            className="col-span-2 md:col-span-2"
          />
          <MInput
            label="PO Date"
            name="poDate"
            type="date"
            value={poDate}
            onChange={(e: any) => setPoDate(e.target.value)}
            disabled={isView}
          />
          <MSelect
            label="Payment"
            name="paymentTerms"
            options={paymentTermOptions}
            value={paymentTerms}
            onChange={(val: any) => setPaymentTerms(val)}
            disabled={isView}
          />
          <MInput
            label="Quot. Ref"
            name="quotationRef"
            value={quotationRef}
            onChange={(e: any) => setQuotationRef(e.target.value)}
            placeholder="Quotation no."
            disabled={isView}
          />
          <MInput
            label="Quot. Date"
            name="quotationDate"
            type="date"
            value={quotationDate}
            onChange={(e: any) => setQuotationDate(e.target.value)}
            disabled={isView}
          />
        </div>

        {/* ─── Row 2: Bill To / Ship To ─── */}
        <div className="grid grid-cols-2 md:grid-cols-1 gap-2">
          <MSelect
            label="Bill To"
            name="billToSubbranch"
            options={subbranchOptions}
            value={billToSubbranch}
            onChange={(val: any) => setBillToSubbranch(val)}
            disabled={isView}
          />

          {/* Ship To Card */}
          <div className="border border-slate-200 rounded-xl overflow-visible">
            <div className="flex">
              <div className="w-6 flex-shrink-0 bg-indigo-400/80 rounded-l-xl flex items-center justify-center">
                <span className="text-[9px] font-bold text-white tracking-wider [writing-mode:vertical-lr] rotate-180">SHIP TO</span>
              </div>
              <div className="flex-1 px-2.5 py-2 space-y-1.5">
                <div className="flex items-center justify-between">
                  {!useManualShip && (
                    <MSelect
                      label="Branch"
                      name="shipToBranch"
                      options={branchOptions}
                      value={shipToBranch}
                      onChange={(val: any) => setShipToBranch(val)}
                      disabled={isView}
                      className="flex-1"
                    />
                  )}
                  {useManualShip && (
                    <MInput
                      label="Name"
                      name="shipName"
                      value={shipToManual.name}
                      onChange={(e: any) => setShipToManual({ ...shipToManual, name: e.target.value })}
                      placeholder="Name"
                      disabled={isView}
                      className="flex-1"
                    />
                  )}
                  {!isView && <label className="inline-flex items-center gap-1 px-2 py-1.5 ml-2 rounded-lg border border-slate-200 bg-white hover:border-slate-300 transition-all cursor-pointer text-[11px] font-medium text-slate-600 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={useManualShip}
                      onChange={(e) => setUseManualShip(e.target.checked)}
                      className="w-3.5 h-3.5 rounded border-slate-300 text-indigo-500 focus:ring-indigo-200"
                    />
                    Manual
                  </label>}
                </div>
                {useManualShip && (
                  <>
                    <MInput
                      label="Address"
                      name="shipAddress"
                      value={shipToManual.address}
                      onChange={(e: any) => setShipToManual({ ...shipToManual, address: e.target.value })}
                      placeholder="Full address"
                      disabled={isView}
                    />
                    <div className="grid grid-cols-3 gap-x-2 gap-y-1">
                      <MInput
                        label="City"
                        name="shipCity"
                        value={shipToManual.city}
                        onChange={(e: any) => setShipToManual({ ...shipToManual, city: e.target.value })}
                        placeholder="City"
                        disabled={isView}
                      />
                      <MInput
                        label="State"
                        name="shipState"
                        value={shipToManual.state}
                        onChange={(e: any) => setShipToManual({ ...shipToManual, state: e.target.value })}
                        placeholder="State"
                        disabled={isView}
                      />
                      <MInput
                        label="Pin"
                        name="shipPincode"
                        value={shipToManual.pincode}
                        onChange={(e: any) => setShipToManual({ ...shipToManual, pincode: e.target.value })}
                        placeholder="Pincode"
                        disabled={isView}
                      />
                    </div>
                    <MInput
                      label="Phone"
                      name="shipPhone"
                      value={shipToManual.phone}
                      onChange={(e: any) => setShipToManual({ ...shipToManual, phone: e.target.value })}
                      placeholder="Phone"
                      disabled={isView}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ─── Line Items Table ─── */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[12px] font-bold text-slate-700 uppercase tracking-wide">Line Items</span>
            {!isView && <button
              onClick={addItem}
              className="flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 px-2 py-1 rounded-lg hover:bg-indigo-50 transition-all"
            >
              <PlusIcon className="w-3.5 h-3.5" /> Add Row
            </button>}
          </div>
          <div className="overflow-x-auto border border-slate-200 rounded-xl">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-2 py-1.5 text-[10px] font-bold text-slate-500 uppercase w-7 text-center">#</th>
                  <th className="px-2 py-1.5 text-[10px] font-bold text-slate-500 uppercase text-left min-w-[200px]">Item / Description</th>
                  <th className="px-2 py-1.5 text-[10px] font-bold text-slate-500 uppercase text-left w-[90px]">HSN</th>
                  <th className="px-2 py-1.5 text-[10px] font-bold text-slate-500 uppercase text-center w-[65px]">Qty</th>
                  <th className="px-2 py-1.5 text-[10px] font-bold text-slate-500 uppercase text-center w-[75px]">Unit</th>
                  <th className="px-2 py-1.5 text-[10px] font-bold text-slate-500 uppercase text-center w-[85px]">Rate ₹</th>
                  <th className="px-2 py-1.5 text-[10px] font-bold text-slate-500 uppercase text-center w-[60px]">GST%</th>
                  <th className="px-2 py-1.5 text-[10px] font-bold text-slate-500 uppercase text-right w-[90px]">Amount ₹</th>
                  {!isView && <th className="px-2 py-1.5 w-8"></th>}
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50/50">
                    <td className="px-2 py-1 text-[11px] text-slate-400 text-center">{idx + 1}</td>
                    <td className="px-1 py-1">
                      <CreatableSelect
                        options={STOCK_ITEM_OPTIONS_PO}
                        value={item.description ? { value: item.description, label: item.description } : null}
                        onChange={(val: any) => updateItem(idx, "description", val?.value || "")}
                        onCreateOption={(inputVal) => updateItem(idx, "description", inputVal)}
                        isDisabled={isView}
                        styles={{
                          ...poSelectStyles,
                          control: (base: any, state: any) => ({
                            ...poSelectStyles.control(base, state),
                            minHeight: "26px",
                            fontSize: "11px",
                          }),
                          indicatorsContainer: (base: any) => ({ ...base, height: "26px" }),
                        }}
                        placeholder="Select or type item..."
                        isClearable
                        formatCreateLabel={(input) => `Custom: "${input}"`}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    </td>
                    <td className="px-1 py-1">
                      <input
                        type="text"
                        className="w-full px-1.5 py-[3px] rounded-lg border border-slate-200 text-[11px] outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100"
                        placeholder="HSN"
                        value={item.hsn}
                        onChange={(e) => updateItem(idx, "hsn", e.target.value)}
                        disabled={isView}
                      />
                    </td>
                    <td className="px-1 py-1">
                      <input
                        type="number"
                        className="w-full px-1.5 py-[3px] rounded-lg border border-slate-200 text-[11px] text-center outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100"
                        min={1}
                        value={item.qty}
                        onChange={(e) => updateItem(idx, "qty", Number(e.target.value))}
                        onWheel={handleWheel}
                        disabled={isView}
                      />
                    </td>
                    <td className="px-1 py-1">
                      <select
                        className="w-full px-1 py-[3px] rounded-lg border border-slate-200 text-[11px] outline-none focus:border-indigo-400"
                        value={item.unit}
                        onChange={(e) => updateItem(idx, "unit", e.target.value)}
                        disabled={isView}
                      >
                        {unitOptions.map((u) => (
                          <option key={u.value} value={u.value}>{u.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-1 py-1">
                      <input
                        type="number"
                        className="w-full px-1.5 py-[3px] rounded-lg border border-slate-200 text-[11px] text-center outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100"
                        min={0}
                        value={item.rate}
                        onChange={(e) => updateItem(idx, "rate", Number(e.target.value))}
                        onWheel={handleWheel}
                        disabled={isView}
                      />
                    </td>
                    <td className="px-1 py-1">
                      <input
                        type="number"
                        className="w-full px-1.5 py-[3px] rounded-lg border border-slate-200 text-[11px] text-center outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100"
                        min={0}
                        max={28}
                        value={item.gstPercent}
                        onChange={(e) => updateItem(idx, "gstPercent", Number(e.target.value))}
                        onWheel={handleWheel}
                        disabled={isView}
                      />
                    </td>
                    <td className="px-2 py-1 text-[12px] font-semibold text-slate-700 text-right">
                      ₹{item.amount.toLocaleString("en-IN")}
                    </td>
                    {!isView && <td className="px-1 py-1 text-center">
                      <button
                        onClick={() => removeItem(idx)}
                        className="text-red-300 hover:text-red-500 transition-colors disabled:opacity-30"
                        disabled={items.length === 1}
                      >
                        <TrashIcon className="w-3.5 h-3.5" />
                      </button>
                    </td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Totals Panel ── */}
          <div className="flex justify-end mt-3">
            <div className="w-72 bg-slate-50 rounded-xl border border-slate-200 p-3 space-y-1.5 text-[12px]">
              <div className="flex justify-between text-slate-500">
                <span>Subtotal</span>
                <span className="font-medium text-slate-700">₹{subtotal.toLocaleString("en-IN")}</span>
              </div>
              <div className="flex justify-between text-slate-500">
                <span>Total GST</span>
                <span className="font-medium text-slate-700">₹{totalGst.toLocaleString("en-IN")}</span>
              </div>
              <div className="flex justify-between items-center text-slate-500">
                <span>Shipping</span>
                <input
                  type="number"
                  className="w-20 px-1.5 py-[2px] rounded-lg border border-slate-200 text-[11px] text-right outline-none focus:border-indigo-400"
                  value={shippingCharges}
                  onChange={(e) => setShippingCharges(Number(e.target.value))}
                  min={0}
                  onWheel={handleWheel}
                  disabled={isView}
                />
              </div>
              <div className="flex justify-between border-t border-slate-200 pt-1.5">
                <span className="font-bold text-slate-800">Grand Total</span>
                <span className="font-bold text-[14px] text-indigo-600">₹{grandTotal.toLocaleString("en-IN")}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ─── Notes ─── */}
        <div>
          <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Notes / Remarks</label>
          <textarea
            className="w-full border border-slate-200 rounded-xl px-3 py-2 text-[12px] outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 resize-none"
            rows={2}
            placeholder="Internal notes..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isView}
          />
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderCreate;
